<template>
  <v-autocomplete
    v-model="selectedCurrency"
    :items="currencies"
    item-text="name"
    item-value="code"
    outlined
    dense
    :label="$t('Currency')"
    :return-object="returnObject"
    :required="!clearable"
    :hide-details="hideDetails"
    :hint="hint"
    :disabled="disabled"
    :rules="rules"
    :persistent-hint="persistentHint"
    :clearable="clearable"
    :filter="filterCurrency"
    @change="change"
  >
    <template #selection="{ item }">
      <div v-if="slim">{{ item.code }}</div>
      <div v-else>{{ item.name }} ({{ item.symbol }}) - {{ item.code }}</div>
    </template>
    <template #item="{ item }">
      <div v-if="slim">{{ item.name }} - {{ item.code }}</div>
      <div v-else>{{ item.name }} ({{ item.symbol }}) - {{ item.code }}</div>
    </template>
  </v-autocomplete>
</template>

<script>
/**
 * CurrencyPicker component
 *
 * 2024-06-09: Export currencies array for reuse in other components
 */
export const currencies = [
  {
    code: 'AUD',
    name: 'Australian Dollars',
    symbol: '$',
    keywords: ['australia', 'aussie'],
  },
  {
    code: 'BRL',
    name: 'Brazilian Reais',
    symbol: 'R$',
    keywords: ['brazil', 'real'],
  },
  {
    code: 'BGN',
    name: 'Bulgarian Leva',
    symbol: 'лв.',
    keywords: ['bulgaria', 'lev'],
  },
  {
    code: 'CAD',
    name: 'Canadian Dollar',
    symbol: '$',
    keywords: ['canada', 'loonie'],
  },
  {
    code: 'CLP',
    name: 'Chilean Pesos',
    symbol: '$',
    keywords: ['chile'],
  },
  {
    code: 'CNY',
    name: 'Chinese Yuan',
    symbol: '￥',
    keywords: ['china', 'renminbi', 'rmb'],
  },
  {
    code: 'COP',
    name: 'Colombian Pesos',
    symbol: '$',
    keywords: ['colombia'],
  },
  {
    code: 'CRC',
    name: 'Costa Rican Colon',
    symbol: '₡',
    keywords: ['costa rica', 'colon'],
  },
  {
    code: 'HRK',
    name: 'Croatian Kuna',
    symbol: 'kn',
    keywords: ['croatia'],
  },
  {
    code: 'CZK',
    name: 'Czech Koruny',
    symbol: 'Kč',
    keywords: ['czech', 'czechia', 'koruna'],
  },
  {
    code: 'DKK',
    name: 'Danish Kroner',
    symbol: 'kr',
    keywords: ['denmark', 'krone'],
  },
  {
    code: 'EGP',
    name: 'Egyptian Pound',
    symbol: 'ج.م',
    keywords: ['egypt'],
  },
  {
    code: 'AED',
    name: 'United Arab Emirates Dirham',
    symbol: 'ﺩ.ﺇ',
    keywords: ['uae', 'dubai', 'emirates', 'abu dhabi'],
  },
  {
    code: 'EUR',
    name: 'Euro',
    symbol: '€',
    keywords: [
      'europe',
      'european',
      'germany',
      'france',
      'italy',
      'spain',
      'portugal',
      'netherlands',
      'belgium',
      'austria',
      'ireland',
      'greece',
      'finland',
    ],
  },
  {
    code: 'GHS',
    name: 'Ghanaian Cedi',
    symbol: 'GH₵',
    keywords: ['ghana'],
  },
  {
    code: 'HKD',
    name: 'Hong Kong Dollars',
    symbol: '$',
    keywords: ['hong kong', 'hk'],
  },
  {
    code: 'HUF',
    name: 'Hungarian Forint',
    symbol: 'Ft',
    keywords: ['hungary', 'forint'],
  },
  {
    code: 'INR',
    name: 'Indian Rupee',
    symbol: '₹',
    keywords: ['india'],
  },
  {
    code: 'IDR',
    name: 'Indonesian Rupiah',
    symbol: 'Rp',
    keywords: ['indonesia'],
  },
  {
    code: 'ILS',
    name: 'Israeli New Shekel',
    symbol: '₪',
    keywords: ['israel', 'shekel', 'nis', 'shekels'],
  },
  {
    code: 'JPY',
    name: 'Japanese Yen',
    symbol: '¥',
    keywords: ['japan', 'yen'],
  },
  {
    code: 'KES',
    name: 'Kenyan Shilling',
    symbol: 'KSh',
    keywords: ['kenya'],
  },
  {
    code: 'MYR',
    name: 'Malaysian Ringgit',
    symbol: 'RM',
    keywords: ['malaysia', 'ringgit'],
  },
  {
    code: 'MXN',
    name: 'Mexican Pesos',
    symbol: '$',
    keywords: ['mexico'],
  },
  {
    code: 'MAD',
    name: 'Moroccan Dirham',
    symbol: 'MAD',
    keywords: ['morocco'],
  },
  {
    code: 'TWD',
    name: 'Taiwan Dollars',
    symbol: '$',
    keywords: ['taiwan', 'taiwanese'],
  },
  {
    code: 'NZD',
    name: 'New Zealand Dollars',
    symbol: '$',
    keywords: ['new zealand', 'kiwi'],
  },
  {
    code: 'NOK',
    name: 'Norwegian Krone',
    symbol: 'kr',
    keywords: ['norway', 'krone'],
  },
  {
    code: 'PEN',
    name: 'Peruvian Sol',
    symbol: 'S/',
    keywords: ['peru', 'sol', 'soles'],
  },
  {
    code: 'PHP',
    name: 'Philippine Peso',
    symbol: '₱',
    keywords: ['philippines', 'filipino'],
  },
  {
    code: 'PLN',
    name: 'Polish Zloty',
    symbol: 'zł',
    keywords: ['poland', 'zloty'],
  },
  {
    code: 'GBP',
    name: 'British Pound Sterling',
    symbol: '£',
    keywords: [
      'uk',
      'united kingdom',
      'england',
      'britain',
      'pound',
      'sterling',
    ],
  },
  {
    code: 'QAR',
    name: 'Qatari Riyal',
    symbol: 'ر.ق',
    keywords: ['qatar'],
  },
  {
    code: 'RON',
    name: 'Romanian Leu',
    symbol: 'lei',
    keywords: ['romania', 'leu'],
  },
  {
    code: 'SAR',
    name: 'Saudi Riyal',
    symbol: 'SR',
    keywords: ['saudi', 'saudi arabia', 'riyal'],
  },
  {
    code: 'SGD',
    name: 'Singapore Dollar',
    symbol: '$',
    keywords: ['singapore'],
  },
  {
    code: 'ZAR',
    name: 'South African Rand',
    symbol: 'R',
    keywords: ['south africa', 'rand'],
  },
  {
    code: 'KRW',
    name: 'South Korean Won',
    symbol: '₩',
    keywords: ['korea', 'korean', 'won'],
  },
  {
    code: 'SEK',
    name: 'Swedish Krona',
    symbol: 'kr',
    keywords: ['sweden', 'krona'],
  },
  {
    code: 'CHF',
    name: 'Swiss Franc',
    symbol: 'CHF',
    keywords: ['switzerland', 'swiss', 'franc'],
  },
  {
    code: 'THB',
    name: 'Thai Baht',
    symbol: '฿',
    keywords: ['thailand', 'baht'],
  },
  {
    code: 'TRY',
    name: 'Turkish Lira',
    symbol: '₺',
    keywords: ['turkey', 'lira'],
  },
  {
    code: 'UGX',
    name: 'Ugandan Shilling',
    symbol: 'USh',
    keywords: ['uganda'],
  },
  {
    code: 'UAH',
    name: 'Ukrainian Hryvnia',
    symbol: '₴',
    keywords: ['ukraine', 'hryvnia'],
  },
  {
    code: 'USD',
    name: 'United States Dollar',
    symbol: '$',
    keywords: ['usa', 'america', 'american', 'us dollar', 'buck'],
  },
  {
    code: 'UYU',
    name: 'Uruguayan Peso',
    symbol: '$U',
    keywords: ['uruguay'],
  },
  {
    code: 'VND',
    name: 'Vietnamese Dong',
    symbol: '₫',
    keywords: ['vietnam', 'dong'],
  },
]

export default {
  name: 'CurrencyPicker',
  props: [
    'value',
    'returnObject',
    'slim',
    'hideDetails',
    'hint',
    'persistentHint',
    'rules',
    'disabled',
    'clearable',
  ],
  data() {
    return {
      selectedCurrency: null,
      currencies,
    }
  },
  created() {
    if (this.returnObject) {
      this.enrichCurrencyValue(this.value)
    }
  },
  methods: {
    change(selectedCurrency) {
      this.$emit('input', selectedCurrency)
    },
    findCurrencyByCode(code) {
      return this.currencies.find(currency => currency.code === code)
    },
    /**
     * Custom filter function for v-autocomplete
     * Searches across code, name, symbol, and keywords
     */
    filterCurrency(item, queryText) {
      if (!queryText) return true

      const search = queryText.toLowerCase().trim()
      if (!search) return true

      // Search in code (e.g., "USD", "ILS")
      if (item.code.toLowerCase().includes(search)) return true

      // Search in name (e.g., "Israeli New Shekel")
      if (item.name.toLowerCase().includes(search)) return true

      // Search in symbol (e.g., "₪", "$")
      if (item.symbol.toLowerCase().includes(search)) return true

      // Search in keywords (e.g., "israel", "shekel", "nis")
      if (
        item.keywords &&
        item.keywords.some(kw => kw.toLowerCase().includes(search))
      ) {
        return true
      }

      return false
    },
    enrichCurrencyValue(value) {
      if (!value) return

      // If only code is provided (or value is a string), get the full currency object
      if (typeof value === 'string') {
        this.selectedCurrency = this.findCurrencyByCode(value)
        // Emit the enriched object back to parent
        if (this.selectedCurrency) {
          this.$emit('input', this.selectedCurrency)
        }
      } else if (value.code && (!value.name || !value.symbol)) {
        const fullCurrency = this.findCurrencyByCode(value.code)
        if (fullCurrency) {
          this.selectedCurrency = fullCurrency
        } else {
          this.selectedCurrency = value
        }
      } else {
        this.selectedCurrency = value
      }
      this.$emit('input', this.selectedCurrency)
    },
  },
  watch: {
    value: {
      handler(newValue) {
        if (this.returnObject) {
          this.enrichCurrencyValue(newValue)
        } else {
          this.selectedCurrency = newValue
        }
      },
      immediate: true,
    },
  },
}
</script>
<style scoped></style>
